// Component Library
import { FsxTableRowStatus } from 'fsx-component-library';

// Data Contracts
import {
  AvailableJudgeUser,
  JudgeReviewFilingSummary,
  JudgeReviewStatus,
  PersonName,
  ReviewFilingCaseSummary,
  ReviewFilingDocumentSummary,
} from '@data-contracts';

// Local
import {
  JudgeReviewJudgeOption,
  JudgeReviewQueueCase,
  JudgeReviewQueueDocument,
  JudgeReviewQueueRow,
} from '../models/judge-review-queue-row.model';

/**
 * Pure mappers from the judge-review `@data-contracts` DTOs to the queue UI
 * models. This is the single translation boundary for the queue feature — the
 * queue service calls these, and everything downstream consumes UI models only.
 */

/**
 * Label for the queue Status column and its filter.
 * `Pending` is a new item; `InProgress` is a draft saved mid-transaction.
 * Clerk filing/document statuses (for example "Accepted") are not valid here.
 */
export function formatJudgeReviewStatus(
  status: string | null | undefined,
): string {
  switch (status) {
    case JudgeReviewStatus.Pending:
      return 'Pending';
    case JudgeReviewStatus.InProgress:
      return 'In Progress';
    default:
      return '';
  }
}

/** Joins a PersonName into "First Last", falling back to `fullName`. */
function displayName(name: PersonName | null | undefined): string {
  if (!name) {
    return '';
  }
  const firstLast = [name.firstName, name.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return firstLast || name.fullName?.trim() || '';
}

/** Two-letter initials from a PersonName (falls back to "?"). */
function nameInitials(name: PersonName | null | undefined): string {
  const first = name?.firstName?.[0] ?? '';
  const last = name?.lastName?.[0] ?? '';
  return (first + last).toUpperCase() || '?';
}

function mapCase(dto: ReviewFilingCaseSummary): JudgeReviewQueueCase {
  return {
    caseId: dto.caseId ?? null,
    caseUniqueId: dto.caseUniqueId ?? null,
    number: dto.number ?? null,
    name: dto.name ?? '',
    caseClass: dto.caseClass ?? '',
    judgeLastName: dto.judge?.name?.lastName ?? '',
    trialBinderId: dto.trialBinderId ?? null,
  };
}

function mapDocument(
  dto: ReviewFilingDocumentSummary,
): JudgeReviewQueueDocument {
  return {
    documentId: dto.documentId ?? null,
    filingTypeId: dto.filingTypeId ?? null,
    filingType: dto.filingType ?? '',
    documentTitle: dto.documentTitle ?? '',
    numberOfBytes: dto.numberOfBytes ?? null,
    reviewStatus: dto.reviewStatus ?? '',
  };
}

/** Maps a filing summary DTO into a queue row UI model. */
export function mapFilingSummaryToQueueRow(
  dto: JudgeReviewFilingSummary,
): JudgeReviewQueueRow {
  const authorizerName = displayName(dto.authorizerName);
  return {
    filingId: dto.filingId ?? null,
    filingUniqueId: dto.filingUniqueId ?? null,
    authorizedDate: dto.authorizedDate ?? null,
    courtTimeZone: dto.courtTimeZone ?? '',
    courtId: dto.courtId ?? null,
    courtOrgId: dto.courtUniqueId ?? null,
    courtName: dto.courtName ?? '',
    authorizerName,
    authorizerLastName: dto.authorizerName?.lastName || authorizerName,
    authorizerOrganizationName: dto.authorizerOrganizationName ?? '',
    status: dto.status ?? '',
    judgeReviewStatus: dto.judgeReviewStatus ?? '',
    isLate: dto.isLate ?? false,
    filerNote: dto.filerNote ?? null,
    // `isLate` here means "delayed" — a clerk chose a future send date — not
    // overdue, so it must not get the red (Danger) left edge (#173996).
    fsxTableRowStatus: FsxTableRowStatus.None,
    cases: (dto.cases ?? []).map(mapCase),
    documents: (dto.documents ?? []).map(mapDocument),
  };
}

/** Maps an available-judge DTO into the selector's judge option UI model. */
export function mapAvailableJudgeToOption(
  dto: AvailableJudgeUser,
): JudgeReviewJudgeOption {
  const rows = (dto.filings ?? []).map(mapFilingSummaryToQueueRow);
  return {
    judgeUniqueId: dto.judgeUniqueId ?? '',
    name: displayName(dto.name),
    initials: nameInitials(dto.name),
    courtName: dto.courtName ?? null,
    filingCount: rows.length,
    rows,
  };
}
